import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3Icon, MapPinIcon, MessageSquareIcon, SparkleIcon, TargetIcon, ZapIcon, LayoutGridIcon, UserIcon, IdCardIcon, MegaphoneIcon, InboxIcon, FileTextIcon, VolleyballIcon, TruckIcon, GaugeIcon, SparklesIcon, SettingsIcon, GlobeIcon, BellIcon, CalendarIcon, ChevronDownIcon, LifeBuoyIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { analyticsIntro, capabilityCards, dashboardNav, supportCard, analyticsSteps, type Bi } from '../../data/communicationsAnalytics';
import { KpiRow, ChannelPerformance, GeographicCoverage, TopicInsight } from './analytics/AnalyticsPanels';
const EASE = [0.23, 1, 0.32, 1] as const;
const CAP_ICONS = {
  global: BarChart3Icon,
  territory: MapPinIcon,
  topic: MessageSquareIcon,
  atna: SparkleIcon
} as const;
const STEP_ICONS = {
  measure: BarChart3Icon,
  identify: MapPinIcon,
  understand: TargetIcon,
  act: ZapIcon
} as const;
const NAV_ICONS: Record<string, typeof UserIcon> = {
  home: LayoutGridIcon,
  users: UserIcon,
  id: IdCardIcon,
  comms: MegaphoneIcon,
  requests: InboxIcon,
  permits: FileTextIcon,
  leisure: VolleyballIcon,
  collect: TruckIcon,
  meters: GaugeIcon,
  atna: SparklesIcon,
  settings: SettingsIcon
};

// Card centres (4 evenly spaced) → dashboard targets (KPI row, map, topics, recommendation).
const FROM_Y = [12.5, 37.5, 62.5, 87.5];
const TO_Y = [22, 50, 64, 82];
function CapabilityConnectors() {
  return <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" data-id="element-2202" data-mp="24afx3gd7wf0b">
      {FROM_Y.map((y, i) => <path key={i} d={`M4 ${y} C 50 ${y}, 50 ${TO_Y[i]}, 100 ${TO_Y[i]}`} fill="none" stroke="#8B7FF7" strokeWidth="1.3" strokeDasharray="3 4" vectorEffect="non-scaling-stroke" data-id="element-2203" data-mp="1zbwaddqsuz6w" data-mpi={i} />)}
    </svg>;
}
function CapabilityCard({
  isFR,
  item
}: {
  isFR: boolean;
  item: (typeof capabilityCards)[number];
}) {
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const Icon = CAP_ICONS[item.key];
  return <div className="relative h-full rounded-2xl border border-[#EEEEF4] bg-white px-5 py-4 flex items-start gap-4 shadow-[0_12px_30px_-24px_rgba(40,36,120,0.45)]" data-id="element-2204" data-mp="1rsvgzl9pqlx9">
      <span className="w-12 h-12 rounded-xl bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2205" data-mp="1i20q23liuy5h">
        <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} data-id="element-2206" data-mp="25sxukvazmt6p" />
      </span>
      <span className="min-w-0" data-id="element-2207" data-mp="2arhi3058cd16">
        <span className="block font-sans text-[15px] font-semibold text-[#121212]" data-id="element-2208" data-mp="2apo5z5bul0ls">{pick(item.title)}</span>
        <span className="block font-sans text-[13px] text-[#5B6478] leading-snug mt-1" data-id="element-2209" data-mp="1wqoii102mrr7">{pick(item.body)}</span>
      </span>
      <span className="hidden 2xl:block absolute -right-[5px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border-2 border-[#6D5DF6] bg-white" aria-hidden="true" data-id="element-2210" data-mp="21s46o1xjpedt" />
    </div>;
}
function Dashboard({
  isFR
}: {
  isFR: boolean;
}) {
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  return <div className="rounded-2xl border border-[#E6E7EE] bg-white overflow-hidden shadow-[0_30px_70px_-40px_rgba(30,30,90,0.45)]" data-id="element-2211" data-mp="hw6qhd8a0teg">
      <div className="h-10 bg-[#141629] px-4 flex items-center justify-between" data-id="element-2212" data-mp="rs1eyoz6lip5">
        <span className="font-sans text-[12px] font-semibold text-white" data-id="element-2213" data-mp="mt6yvuslm88q">Windsor</span>
        <span className="flex items-center gap-4 text-white/85" aria-hidden="true" data-id="element-2214" data-mp="mr41uqt13eui">
          <span className="flex items-center gap-1 font-sans text-[10px]" data-id="element-2215" data-mp="3v99tayupa0f4">
            <GlobeIcon className="w-3.5 h-3.5" data-id="element-2216" data-mp="3qb8c6jgu4b6a" /> {isFR ? 'FR' : 'EN'}
          </span>
          <BellIcon className="w-3.5 h-3.5" data-id="element-2217" data-mp="8tp859vqskwq" />
          <span className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center font-sans text-[9.5px] font-semibold" data-id="element-2218" data-mp="8viesp6cv54q">JD</span>
        </span>
      </div>
      <div className="flex bg-[#FAFAFC]" data-id="element-2219" data-mp="3ch7msgbrw0il">
        <nav className="hidden lg:flex w-[170px] shrink-0 border-r border-[#EEEEF4] bg-white px-2.5 py-4 flex-col" aria-label={isFR ? 'Navigation de l’application' : 'App navigation'} data-id="element-2220" data-mp="3cg601ele9pl1">
          <ul className="space-y-0.5" data-id="element-2221" data-mp="2apxym8kzr21i">
            {dashboardNav.map(n => {
            const Icon = NAV_ICONS[n.key];
            const active = n.key === 'comms';
            return <li key={n.key} className={`flex items-center gap-2.5 rounded-md px-2.5 py-2 font-sans text-[11px] ${active ? 'bg-[#141629] text-white' : 'text-[#3A4050]'}`} data-id="element-2222" data-mp="2jqmcn3adx2ee">
                  <Icon className={`w-3.5 h-3.5 ${active ? 'text-[#A99CF9]' : 'text-[#9AA0AE]'}`} strokeWidth={1.8} aria-hidden="true" data-id="element-2223" data-mp="2ygbsm4n4zayq" />
                  {pick(n.label)}
                </li>;
          })}
          </ul>
          <div className="mt-auto rounded-lg border border-[#EEEEF4] px-2.5 py-2.5" data-id="element-2224" data-mp="37g77okjtbq41">
            <p className="flex items-center gap-1.5 font-sans text-[9px] font-semibold tracking-[0.14em] uppercase text-[#3A4050]" data-id="element-2225" data-mp="194i0nuxk7du4">
              <LifeBuoyIcon className="w-3 h-3" aria-hidden="true" data-id="element-2226" data-mp="15krcyzop7u8y" />
              {pick(supportCard.label)}
            </p>
            <p className="font-sans text-[9.5px] text-[#6B7180] leading-snug mt-1" data-id="element-2227" data-mp="10tstjr8eyg1h">{pick(supportCard.body)}</p>
            <p className="font-sans text-[9.5px] text-[#4F46E5] mt-2" data-id="element-2228" data-mp="vvzpp4ul1y4d">{pick(supportCard.link)}</p>
          </div>
        </nav>

        <div className="flex-1 min-w-0 p-4 lg:p-5" data-id="element-2229" data-mp="rv7ew762bl5z">
          <div className="flex items-start justify-between gap-3" data-id="element-2230" data-mp="1ok58vn5znf23">
            <div data-id="element-2231" data-mp="1oh9da5hm52gt">
              <p className="font-sans text-[18px] font-semibold text-[#121212]" data-id="element-2232" data-mp="1jkscobt88vmu">Communications</p>
              <p className="flex items-center gap-1.5 font-sans text-[12px] text-[#3A4050] mt-1.5" data-id="element-2233" data-mp="1fioy3eb9haxc">
                <BarChart3Icon className="w-3.5 h-3.5 text-[#8B7FF7]" aria-hidden="true" data-id="element-2234" data-mp="1ak48e8tmy9n5" />
                {isFR ? 'Aperçu général' : 'Overview'}
              </p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-lg border border-[#E6E7EE] bg-white px-3 py-1.5 font-sans text-[10.5px] text-[#3A4050] whitespace-nowrap" data-id="element-2235" data-mp="41fij2v9731b">
              <CalendarIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2236" data-mp="mnp0xpzkxhr" />
              {isFR ? '30 derniers jours' : 'Last 30 days'}
              <ChevronDownIcon className="w-3 h-3" aria-hidden="true" data-id="element-2237" data-mp="3nqkd2qe0dspq" />
            </span>
          </div>
          <div className="mt-3" data-id="element-2238" data-mp="3rrvtq6ewn3ls">
            <KpiRow isFR={isFR} data-id="element-2239" data-mp="3rr4c5zi576ei" />
          </div>
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr_1fr] gap-3" data-id="element-2240" data-mp="3ru09pmq2vt2f">
            <ChannelPerformance isFR={isFR} data-id="element-2241" data-mp="3dyo4gleaqk79" />
            <GeographicCoverage isFR={isFR} data-id="element-2242" data-mp="3irqy0tj2ptng" />
            <TopicInsight isFR={isFR} data-id="element-2243" data-mp="3isse5cey56c1" />
          </div>
        </div>
      </div>
    </div>;
}
export function CommunicationsAnalytics() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const body = isFR ? analyticsIntro.body.fr : analyticsIntro.body.en;
  return <div className="relative max-w-[1600px] mx-auto" data-id="element-2244" data-mp="300q26ed92e84">
      <div className="pointer-events-none absolute -left-40 top-10 w-[480px] h-[480px] rounded-full bg-[#F1F0FD] opacity-70 blur-[120px]" aria-hidden="true" data-id="element-2245" data-mp="300gcyt014b3h" />

      <motion.div initial={{
      opacity: 0,
      y: 12
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.3,
      ease: EASE
    }} className="relative text-center max-w-4xl mx-auto" data-id="element-2246" data-mp="2bgmm8cx2rc9f">
        <p className="font-sans text-[12.5px] font-semibold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-2247" data-mp="2lgn7w5if24gs">{pick(analyticsIntro.eyebrow)}</p>
        <h2 className="font-serif text-[2.3rem] sm:text-5xl lg:text-[3.6rem] leading-[1.05] tracking-tight mt-5" data-id="element-2248" data-mp="2ufqu3icvqzzi">
          <span className="block text-[#121212]" data-id="element-2249" data-mp="392k1m1g5r1h5">{pick(analyticsIntro.line1)}</span>
          <span className="block text-[#4F46E5]" data-id="element-2250" data-mp="344il9oa49g9d">{pick(analyticsIntro.line2)}</span>
        </h2>
        <p className="font-sans text-[16px] md:text-[17px] text-[#5B6478] leading-relaxed mt-5" data-id="element-2251" data-mp="3i3s0akml64br">
          {body.map(l => <span key={l} className="lg:block" data-id="element-2252" data-mp="3d71le0miperx">
              {l}{' '}
            </span>)}
        </p>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 14
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.3,
      delay: 0.06,
      ease: EASE
    }} className="relative mt-12 grid grid-cols-1 2xl:grid-cols-[minmax(0,0.25fr)_48px_minmax(0,1fr)] gap-5 2xl:gap-0 items-stretch" data-id="element-2253" data-mp="oeuths2kxqyl">
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-1 2xl:grid-rows-4 gap-3 2xl:gap-4" data-id="element-2254" data-mp="5nbrnokakiy5">
          {capabilityCards.map(c => <CapabilityCard key={c.key} isFR={isFR} item={c} data-id="element-2255" data-mp="emowu51duld2" />)}
        </div>
        <div className="hidden 2xl:block" data-id="element-2256" data-mp="ekvq6pqrs152">
          <CapabilityConnectors data-id="element-2257" data-mp="jegs2svhhf9j" />
        </div>
        <div className="min-w-0" data-id="element-2258" data-mp="jf9r17o7b0h4">
          <Dashboard isFR={isFR} data-id="element-2259" data-mp="3i3yu4r8i8z5g" />
        </div>
      </motion.div>

      <ol className="relative mt-10 pt-8 border-t border-[#EEEEF4] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0 lg:divide-x divide-[#EEEEF4]" data-id="element-2260" data-mp="3i1kyoh9wxkxd">
        {analyticsSteps.map(s => {
        const Icon = STEP_ICONS[s.key];
        return <li key={s.key} className="flex items-start gap-4 lg:px-8" data-id="element-2261" data-mp="3al7nmdh62cxa">
              <span className="w-14 h-14 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2262" data-mp="35k1uwfniapnr">
                <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.6} data-id="element-2263" data-mp="31ion400usce7" />
              </span>
              <span className="min-w-0" data-id="element-2264" data-mp="2wlpujlwu8di4">
                <span className="block font-sans text-[17px] font-semibold text-[#121212]" data-id="element-2265" data-mp="2rtewyppkl3sq">{pick(s.title)}</span>
                <span className="block font-sans text-[14px] text-[#5B6478] leading-snug mt-1" data-id="element-2266" data-mp="2mu26luzhpele">{pick(s.body)}</span>
              </span>
            </li>;
      })}
      </ol>
    </div>;
}